import { supabase, applyOrgFilter, getActiveOrgOverride, signImagesDeep } from '../supabaseClient'
import { normalizeSportValue } from '../../constants/sports'
import { normalizeAgePayload } from '../../utils/ageLimits'
import { getCurrentStaff } from '../supabase'
import { canCreate } from './billing'

const extractCategoriesFromRelation = (group) => {
  if (!group?.grupos_categorias) return []

  return group.grupos_categorias
    .map((rel) => {
      if (!rel) return null
      const base = rel.categorias || rel.categoria
      if (!base) return null

      return {
        ...base,
        id: base.id || rel.categoria_id
      }
    })
    .filter(Boolean)
}

const normalizeGroupCategories = (group) => {
  if (!group) return group
  const categorias = extractCategoriesFromRelation(group)
  return {
    ...group,
    categorias,
    categoria_ids: categorias.map((cat) => cat.id)
  }
}

const normalizeGroupCollection = (collection) => {
  if (!collection) return collection
  if (Array.isArray(collection)) {
    return collection.map((item) => normalizeGroupCategories(item))
  }
  return normalizeGroupCategories(collection)
}


const sanitizeCategoryIds = (ids = []) =>
  Array.from(
    new Set(
      (Array.isArray(ids) ? ids : [])
        .map((value) => (value ?? '').toString().trim())
        .filter(Boolean)
    )
  )

// Guarda las categorias de un grupo SIN borrar primero: agrega las nuevas y despues quita
// las que sobran. Si la base rechaza una (20261002: otro deporte u otra org), no se borra
// nada y el error vuelve a quien llamo.
export const syncGroupCategories = async (groupId, categoriaIds = []) => {
  const ids = sanitizeCategoryIds(categoriaIds)
  if (!groupId) {
    return { data: null, error: new Error('El ID del grupo es requerido para actualizar categorías') }
  }

  const { data: actuales, error: readError } = await supabase
    .from('grupos_categorias')
    .select('categoria_id')
    .eq('grupo_id', groupId)
  if (readError) return { data: null, error: readError }

  const tiene = new Set((actuales || []).map((row) => row.categoria_id))
  const nuevas = ids.filter((id) => !tiene.has(id))
  const sobran = [...tiene].filter((id) => !ids.includes(id))

  if (nuevas.length > 0) {
    const { error } = await supabase
      .from('grupos_categorias')
      .insert(nuevas.map((categoriaId) => ({ grupo_id: groupId, categoria_id: categoriaId })))
      .select()
    if (error) return { data: null, error }
  }

  if (sobran.length > 0) {
    const { error } = await supabase
      .from('grupos_categorias')
      .delete()
      .eq('grupo_id', groupId)
      .in('categoria_id', sobran)
    if (error) return { data: null, error }
  }

  return { data: ids, error: null }
}

// Groups data
export const getStaffGroups = async (staffId) => {
  const { data, error } = await supabase
    .from('staff_grupos')
        .select(`
      *,
      grupos!inner(
        *,
        grupos_categorias(
          categoria_id,
          categorias(*)
        ),
        jugadores_grupos(
          id,
          jugador_id,
          estatus,
          activo,
          deleted
        ),
        series_entrenamiento(id)
      ),
      staff(*)
    `)
    .eq('staff_id', staffId)
    .eq('activo', true)
    .in('grupos.estatus', ['Activo', 'Inactivo', 'En preparación']);

  const normalizedData = data
    ? data.map((record) => ({
        ...record,
        grupos: record.grupos ? normalizeGroupCategories(record.grupos) : null
      }))
    : data;

  return { data: normalizedData, error };
}

// Backward compatibility
export const getCoachGroups = getStaffGroups

// Get all groups for admin (exclude Cerrado and Archivado)
export const getAllGroups = async (orgId = null) => {
  let query = supabase
    .from('grupos')
    .select(`
      *,
      grupos_categorias(
        categoria_id,
        categorias(*)
      )
    `)
    .in('estatus', ['Activo', 'Inactivo', 'En preparación'])
    .order('nombre_del_grupo')

  query = applyOrgFilter(query, orgId)

  const { data, error } = await query
  const normalizedData = normalizeGroupCollection(data);
  return { data: normalizedData, error }
}

// Get groups by ids with categories (includes all statuses)
export const getGroupsByIds = async (groupIds = []) => {
  const ids = Array.from(new Set((groupIds || []).filter(Boolean)));
  if (ids.length === 0) return { data: [], error: null };

  const { data, error } = await supabase
    .from('grupos')
    .select(`
      *,
      grupos_categorias(
        categoria_id,
        categorias(*)
      )
    `)
    .in('id', ids)
    .order('nombre_del_grupo');

  const normalizedData = normalizeGroupCollection(data);
  return { data: normalizedData, error };
};

// Get all groups with their assigned staff for admin view
export const getAllGroupsWithStaff = async (orgId = null, { includeArchived = false } = {}) => {
  const estatusList = includeArchived
    ? ['Activo', 'Inactivo', 'En preparación', 'Cerrado', 'Archivado']
    : ['Activo', 'Inactivo', 'En preparación']
  let query = supabase
    .from('grupos')
    .select(`
      *,
      grupos_categorias(
        categoria_id,
        categorias(*)
      ),
      staff_grupos(
        *,
        staff(*)
      ),
      jugadores_grupos(
        id,
        jugador_id,
        estatus,
        activo,
        deleted
      ),
      series_entrenamiento(id)
    `)
    .in('estatus', estatusList)
    .order('nombre_del_grupo')

  query = applyOrgFilter(query, orgId)

  const { data, error } = await query
  const normalizedDataWithStaff = normalizeGroupCollection(data);
  return { data: normalizedDataWithStaff, error }
}

// Backward compatibility
export const getAllGroupsWithCoaches = getAllGroupsWithStaff

// Count active, non-deleted, non-duplicate players in a group's jugadores_grupos array
export const countActiveGroupPlayers = (jugadoresGrupos = []) => {
  const seen = new Set()
  let count = 0
  for (const jg of jugadoresGrupos) {
    if (jg.activo === false || jg.deleted === true) continue
    if (jg.estatus && jg.estatus !== 'Activo') continue
    const jId = jg.jugador_id
    if (seen.has(jId)) continue
    seen.add(jId)
    count++
  }
  return count
}

// Players data
export const getGroupPlayers = async (groupId) => {
  const baseQuery = () => supabase
    .from('jugadores_grupos')
    .select(`
      *,
      jugadores!inner(
        *,
        jugadores_meta(
          meta_key,
          meta_value
        )
      ),
      grupos (
        id,
        nombre_del_grupo
      )
    `)
    .eq('grupo_id', groupId)
    .eq('deleted', false)
    .eq('jugadores.deleted', false)
    .eq('jugadores.activo', true)
    .neq('jugadores.estatus', 'Baja')
    .eq('activo', true)
    .eq('deleted', false)
    .eq('estatus', 'Activo');

  const extendedQuery = () => supabase
    .from('jugadores_grupos')
    .select(`
      *,
      jugadores!inner(
        *,
        jugadores_meta(
          meta_key,
          meta_value
        )
      ),
      grupos (
        id,
        nombre_del_grupo,
        deporte
      )
    `)
    .eq('grupo_id', groupId)
    .eq('deleted', false)
    .eq('jugadores.deleted', false)
    .eq('jugadores.activo', true)
    .neq('jugadores.estatus', 'Baja')
    .eq('activo', true)
    .eq('deleted', false)
    .eq('estatus', 'Activo');

  const { data, error } = await extendedQuery();
  const response = error ? await baseQuery() : { data, error };

  // Ordenar en el cliente por estatus y nombre
  if (response.data) {
    response.data.sort((a, b) => {
      // Primero por estatus: Activo > Pendiente > Inactivo
      const estatusOrder = { 'Activo': 0, 'Pendiente': 1, 'Inactivo': 2 }
      const orderA = estatusOrder[a.estatus] ?? 999
      const orderB = estatusOrder[b.estatus] ?? 999

      if (orderA !== orderB) {
        return orderA - orderB
      }

      // Luego por nombre (ascendente)
      return a.jugadores.nombre_completo.localeCompare(b.jugadores.nombre_completo)
    })
    // Las fotos viven en un bucket PRIVADO: `imagen_url` guardada no sirve, hay que
    // firmarla. La gestion de jugadores lo hacia y esta consulta no, y por eso en la
    // ficha del grupo, la asistencia, el roster y la inscripcion las fotos salian
    // vacias mientras en Jugadores si se veian (el dueno lo noto el 2026-09-04).
    await signImagesDeep(response.data)
  }

  return { data: response.data, error: response.error }
}

// Group Management Functions
export const getGroupById = async (groupId) => {
  try {
    const { data, error } = await supabase
      .from('grupos')
      .select(`
        *,
        staff_grupos(
          *,
          staff(id, nombre_completo, email, imagen_url, imagen_path)
        ),
        grupos_categorias(
          categoria_id,
          categorias(*)
        )
      `)
      .eq('id', groupId)
      .single();

    const normalizedGroup = normalizeGroupCategories(data);
    if (!error) await signImagesDeep(normalizedGroup);
    return { data: normalizedGroup, error };
  } catch (error) {
    console.error('Error getting group by ID:', error);
    return { data: null, error };
  }
};

export const createGroup = async (groupData) => {
  try {
    // Get current user for audit trail
    const { data: { user } } = await supabase.auth.getUser();
    const { data: currentStaff } = await getCurrentStaff();

    // Sin org no se adivina: caer a la org por defecto guardaba el grupo en otra org.
    const orgId = getActiveOrgOverride() || groupData?.org_id || currentStaff?.org_id;
    if (!orgId) {
      return { data: null, error: { code: 'ORG_OBLIGATORIA', message: 'No se pudo saber tu organización. Recarga la página e intenta de nuevo.' } };
    }

    if (!(await canCreate(orgId, 'groups'))) {
      return { data: null, error: { code: 'PLAN_LIMIT', resource: 'groups', message: 'Alcanzaste el límite de grupos de tu plan. Mejora tu plan para agregar más.' } };
    }

    if (!groupData?.deporte) {
      return { data: null, error: { code: 'DEPORTE_OBLIGATORIO', message: 'El deporte es obligatorio' } };
    }

    // Extract staff assignments (supports both single and multiple staff)
    const {
      staff_id,
      coach_id,
      staff_rol,
      coach_rol,
      staff_assignments, // New: array of {staff_id, rol}
      categoria_ids: rawCategoriaIds,
      categorias: categoriasPayload,
      ...groupDataWithoutStaff
    } = groupData;

    const agePayload = normalizeAgePayload(groupDataWithoutStaff);
    const categoryIds = sanitizeCategoryIds(
      rawCategoriaIds ||
        (Array.isArray(categoriasPayload) ? categoriasPayload.map((cat) => cat.id).filter(Boolean) : [])
    );

    // Generate UUID and add created_by for audit trail
    const groupDataWithSource = {
      id: crypto.randomUUID(),
      ...groupDataWithoutStaff,
      ...agePayload,
      deporte: normalizeSportValue(groupDataWithoutStaff.deporte),
      org_id: orgId,
      created_by: user?.id || 'system' // User ID from auth for audit trail
    };

    const { data: createdGroup, error: groupError } = await supabase
      .from('grupos')
      .insert(groupDataWithSource)
      .select()
      .single();

    if (groupError) throw groupError;

    let categoriasError = null;
    if (categoryIds.length > 0) {
      const { error: categoriesError } = await syncGroupCategories(createdGroup.id, categoryIds);
      categoriasError = categoriesError || null;
    }

    // Handle staff assignments
    let assignmentsToCreate = [];

    // If staff_assignments array is provided (new format - multiple staff)
    if (staff_assignments && Array.isArray(staff_assignments) && staff_assignments.length > 0) {
      assignmentsToCreate = staff_assignments.map(assignment => ({
        staff_id: assignment.staff_id,
        grupo_id: createdGroup.id,
        rol: assignment.rol || 'Coach',
        activo: true
      }));
    }
    // Backward compatibility: single staff_id/coach_id (old format)
    else {
      const finalStaffId = staff_id || coach_id;
      const finalStaffRol = staff_rol || coach_rol;

      if (finalStaffId && finalStaffId !== '') {
        assignmentsToCreate = [{
          staff_id: finalStaffId,
          grupo_id: createdGroup.id,
          rol: finalStaffRol || 'Head Coach',
          activo: true
        }];
      }
    }

    // Insert staff assignments if any
    if (assignmentsToCreate.length > 0) {
      const { error: staffGroupError } = await supabase
        .from('staff_grupos')
        .insert(assignmentsToCreate);

      if (staffGroupError) {
        console.error('Error creating staff-group relationships:', staffGroupError);
        // Don't throw error here, group was created successfully
      }
    }

    return { data: createdGroup, error: null, categoriasError };
  } catch (error) {
    return { data: null, error };
  }
};

export const updateGroup = async (groupId, groupData) => {
  try {
    const payload = { ...groupData };
    Object.assign(payload, normalizeAgePayload(groupData));

    const hasCategoryPayload = Object.prototype.hasOwnProperty.call(groupData, 'categoria_ids') ||
      Object.prototype.hasOwnProperty.call(groupData, 'categorias');

    const categoryIds = hasCategoryPayload
      ? sanitizeCategoryIds(
          payload.categoria_ids ||
            (Array.isArray(payload.categorias) ? payload.categorias.map((cat) => cat.id).filter(Boolean) : [])
        )
      : null;

    delete payload.categoria_ids;
    delete payload.categorias;

    if (payload.deporte) {
      payload.deporte = normalizeSportValue(payload.deporte);
    }

    const { data, error } = await supabase
      .from('grupos')
      .update(payload)
      .eq('id', groupId)
      .select('id');

    if (error) throw error;

    const updatedGroup = Array.isArray(data) && data.length > 0 ? data[0] : null;

    let categoriasError = null;
    if (hasCategoryPayload) {
      const { error: categoriesError } = await syncGroupCategories(groupId, categoryIds || [])
      categoriasError = categoriesError || null
    }

    await supabase.functions.invoke('recalculate-player-metadata', {
      body: { group_id: groupId }
    });

    return { data: updatedGroup, error: null, categoriasError };
  } catch (error) {
    return { data: null, error };
  }
};

export const deleteGroup = async (groupId) => {
  try {
    const { data, error } = await supabase
      .from('grupos')
      .delete()
      .eq('id', groupId);

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// ============================================================================
// Staff-Group Management Functions
// ============================================================================

/**
 * Get all staff assigned to a specific group
 * @param {string} groupId - The group ID
 * @returns {Promise} Staff members with their roles
 */
export const getGroupStaff = async (groupId) => {
  try {
    const { data, error } = await supabase
      .from('staff_grupos')
      .select(`
        *,
        staff (
          id,
          nombre_completo,
          email,
          rol,
          activo
        )
      `)
      .eq('grupo_id', groupId)
      .eq('activo', true)
      .order('rol', { ascending: true });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Assign a staff member to a group
 * @param {string} groupId - The group ID
 * @param {string} staffId - The staff member ID
 * @param {string} rol - The role (e.g., 'Head Coach', 'Asistente', 'Manager')
 * @returns {Promise}
 */
export const assignStaffToGroup = async (groupId, staffId, rol = 'Coach', options = {}) => {
  try {
    const {
      tarjeton_entrenador = false,
      tarjeton_asistente = false
    } = options || {};
    // Check if assignment already exists
    const { data: existing, error: existingError } = await supabase
      .from('staff_grupos')
      .select('*')
      .eq('grupo_id', groupId)
      .eq('staff_id', staffId)
      .maybeSingle();

    if (existingError) {
      throw existingError;
    }

    // If exists and is inactive, reactivate it
    if (existing && !existing.activo) {
      const { data, error } = await supabase
        .from('staff_grupos')
        .update({
          activo: true,
          rol: rol,
          tarjeton_entrenador: tarjeton_entrenador,
          tarjeton_asistente: tarjeton_asistente,
          deleted: false,
          deleted_at: null
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    }

    // If already active, just update the role
    if (existing && existing.activo) {
      const { data, error } = await supabase
        .from('staff_grupos')
        .update({
          rol: rol,
          tarjeton_entrenador: tarjeton_entrenador,
          tarjeton_asistente: tarjeton_asistente
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    }

    // Create new assignment
    const { data, error } = await supabase
      .from('staff_grupos')
      .insert({
        staff_id: staffId,
        grupo_id: groupId,
        rol: rol,
        tarjeton_entrenador: tarjeton_entrenador,
        tarjeton_asistente: tarjeton_asistente,
        activo: true
      })
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Remove a staff member from a group (soft delete)
 * @param {string} groupId - The group ID
 * @param {string} staffId - The staff member ID
 * @returns {Promise}
 */
export const removeStaffFromGroup = async (groupId, staffId) => {
  try {
    const { data, error } = await supabase
      .from('staff_grupos')
      .update({
        activo: false
      })
      .eq('grupo_id', groupId)
      .eq('staff_id', staffId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Update a staff member's role in a group
 * @param {string} groupId - The group ID
 * @param {string} staffId - The staff member ID
 * @param {string} newRol - The new role
 * @returns {Promise}
 */
export const updateStaffRole = async (groupId, staffId, newRol, options = {}) => {
  try {
    const {
      tarjeton_entrenador = undefined,
      tarjeton_asistente = undefined
    } = options || {};
    const updatePayload = { rol: newRol };
    if (typeof tarjeton_entrenador === 'boolean') {
      updatePayload.tarjeton_entrenador = tarjeton_entrenador;
    }
    if (typeof tarjeton_asistente === 'boolean') {
      updatePayload.tarjeton_asistente = tarjeton_asistente;
    }
    const { data, error} = await supabase
      .from('staff_grupos')
      .update(updatePayload)
      .eq('grupo_id', groupId)
      .eq('staff_id', staffId)
      .eq('activo', true)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const getGroupWithPlayers = async (groupId) => {
  const { data, error } = await supabase
    .from('grupos')
    .select(`
      *,
      jugadores_grupos!inner(
        id,
        estatus,
        activo,
        deleted,
        jugadores!inner(*)
      )
    `)
    .eq('id', groupId)
    .eq('jugadores_grupos.deleted', false)
    .eq('jugadores_grupos.activo', true)
    .eq('jugadores_grupos.jugadores.deleted', false)
    .eq('jugadores_grupos.jugadores.activo', true)
    .neq('jugadores_grupos.jugadores.estatus', 'Baja')
    .eq('jugadores_grupos.estatus', 'Activo')
    .single();

  return { data, error };
};
